import { createTamagui } from 'tamagui';
import { config as defaultConfig } from '@tamagui/config/v3';
import { colors, colorsDark } from './theme/colors';

const defaultTokens = defaultConfig.tokens as Record<string, Record<string, unknown>>;
const zeroRadius = Object.fromEntries(
  Object.keys(defaultTokens.radius || {}).map((k) => [k, 0] as const)
) as Record<string, number>;

// Build theme objects: only string values (polaroidRainbow is array, used directly)
const toThemeEntry = (c: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(c).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
  );

const lightTheme = {
  ...toThemeEntry(colors),
  // Map Tamagui standard tokens to our custom values so built-in components
  // (Label, H1-H6, Paragraph, etc.) pick up the right colors per theme.
  background: colors.backgroundPage,
  color: colors.textOnSurface,
  borderColor: colors.borderDefault,
  placeholderColor: colors.textSubdued,
};
const darkTheme = {
  ...toThemeEntry(colorsDark),
  background: colorsDark.backgroundPage,
  color: colorsDark.textOnSurface,
  borderColor: colorsDark.borderDefault,
  placeholderColor: colorsDark.textSubdued,
};

/**
 * App breakpoints (min-width, mobile-first).
 * Must stay in sync with ADR 0012.
 *
 * Usage (responsive props):  <Stack $md={{ flexDirection: 'row' }} />
 * Usage (conditional render): const { md } = useMedia(); {md && <Sidebar />}
 */
const media = {
  sm: { maxWidth: 767 },   // phone portrait  (< 768px) — default styles target this
  md: { minWidth: 768 },   // tablet portrait (≥ 768px)
  lg: { minWidth: 1024 },  // tablet landscape / small desktop (≥ 1024px)
  xl: { minWidth: 1280 },  // desktop (≥ 1280px)
} as const;

const appConfig = createTamagui({
  ...defaultConfig,
  media,
  tokens: {
    ...defaultTokens,
    radius: { ...(defaultTokens.radius as Record<string, number>), ...zeroRadius },
    color: {
      ...(defaultTokens.color as Record<string, string>),
      ...lightTheme, // default token values (light) for $backgroundPage etc.
    },
  },
  themes: {
    light: lightTheme,
    dark: darkTheme,
  },
});

export type Conf = typeof appConfig;

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Tamagui {
    type Config = Conf
  }
}

export default appConfig;
